import {
  type Href,
  Redirect,
  router,
  type ScreenProps,
  Tabs,
  usePathname,
} from "expo-router"
import type { NavigationState } from "@react-navigation/native"
import { GestureHandlerRootView } from "react-native-gesture-handler"
import { BottomSheetModalProvider } from "@gorhom/bottom-sheet"

import { BFColors } from "@/constants/Colors"
import { useStore } from "@nanostores/react"
import { $authToken } from "@/stores/auth"

import CustomTabIcon from "@/components/navigation/CustomTabIcon"
import OAuthProvider from "@/components/auth/OAuthProvider"

import CalendarDots from "phosphor-react-native/src/icons/CalendarDots"
import House from "phosphor-react-native/src/icons/House"
import Tray from "phosphor-react-native/src/icons/Tray"
import MagnifyingGlass from "phosphor-react-native/src/icons/MagnifyingGlass"
import Star from "phosphor-react-native/src/icons/Star"
import CurrencyCircleDollar from "phosphor-react-native/src/icons/CurrencyCircleDollar"

import {
  $activeTab,
  ActiveTabType,
  setActiveTab,
  TAB_NAMES,
} from "@/stores/appStore"
import { useUser } from "@/queries/user"
import config from "@/config"

import * as tabEvents from "@/analytics/events/bottom-tabs"
import { useCheckUpdates } from "@/hooks/useCheckUpdates"
import { useIsDesktop } from "@/hooks/useIsDesktop"
import { BFDeskopHeader } from "@/components/standard/screen/BFDeskopHeader"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import { setOriginalUrlBeforeLoginRedirect } from "@/stores/originalLoginUrl"
import { Pressable } from "react-native"
import React, { useMemo } from "react"
import type IconProps from "phosphor-react-native"

export const TAB_BAR_HEIGHT = 60

const getTabConfig = (): Array<Omit<BottomTabConfig, "activeTab">> => [
  {
    name: "index",
    title: TAB_NAMES[ActiveTabType.Home],
    label: TAB_NAMES[ActiveTabType.Home],
    Icon: House,
    path: "/",
  },
  {
    name: "properties",
    title: TAB_NAMES[ActiveTabType.Properties],
    label: TAB_NAMES[ActiveTabType.Properties],
    defaultIconScale: 0.9,
    Icon: Tray,
  },
  {
    name: "analyze",
    title: TAB_NAMES[ActiveTabType.Analyze],
    label: TAB_NAMES[ActiveTabType.Analyze],
    Icon: MagnifyingGlass,
  },
  {
    name: "loans",
    title: TAB_NAMES[ActiveTabType.Loans],
    label: TAB_NAMES[ActiveTabType.Loans],
    Icon: CurrencyCircleDollar,
  },
  {
    name: "rewards",
    title: TAB_NAMES[ActiveTabType.Rewards],
    label: TAB_NAMES[ActiveTabType.Rewards],
    Icon: Star,
    hide: true,
  },
  {
    name: "events",
    title: TAB_NAMES[ActiveTabType.Events],
    label: TAB_NAMES[ActiveTabType.Events],
    Icon: CalendarDots,
  },
]

const RegisterRedirect = () => {
  const pathName = usePathname()
  setOriginalUrlBeforeLoginRedirect(pathName as Href)

  return <Redirect href="/register" />
}

interface BottomTabConfig {
  name: string
  path?: Href
  title: string
  label: string
  activeTab: string
  Icon: React.FC<IconProps.IconProps>
  defaultIconScale?: number
  badgeCount?: number | null
  onPress?: () => void
  hide?: boolean
}

const HIDE_TABS_FOR_THESE_PATHS = [
  "/loans/setup/property",
  "/loans/setup/qualifications",
  "/loans/setup/product",
  "/loans/setup/submit",
  "/loans/setup/success",
]

const createTabScreenProps = (config: BottomTabConfig): ScreenProps => {
  return {
    name: config.name,
    listeners: {
      tabPress: () => {
        tabEvents.tabClicked({
          currentTab: config.activeTab,
          tabText: config.title,
        })
      },
    },
    options: {
      title: config.title,
      href: config.hide ? null : undefined,
      tabBarButton: config.hide
        ? undefined
        : () => (
            <Pressable
              className="items-center justify-center flex-1"
              testID={`tab-${config.title.toLowerCase()}`}
              onPress={
                config.onPress ||
                (() => {
                  router.navigate(config.path || `/${config.name}`)
                })
              }
            >
              <CustomTabIcon
                Icon={config.Icon}
                label={config.label}
                focused={config.activeTab === config.name}
                defaultScale={config.defaultIconScale || 1}
                badge={config.badgeCount || null}
              />
            </Pressable>
          ),
    },
  }
}

export default () => {
  useCheckUpdates()
  const authToken = useStore($authToken)
  const activeTab = useStore($activeTab)
  const { data: user } = useUser()
  const isDesktop = useIsDesktop()
  const { bottom } = useSafeAreaInsets()
  const pathname = usePathname()

  // @ts-expect-error for debugging
  window.me = user

  const handleTabChange = (e: {
    data: {
      state: NavigationState
    }
  }) => {
    const stateIndex = e.data.state.index
    const route = e.data.state.routes[stateIndex]
    const tabName = route.name

    setActiveTab(tabName as ActiveTabType)
  }

  const iconConfig = useMemo(() => {
    const config = getTabConfig()

    if (user?.badges?.unreadAnalysisCount) {
      config.find((c) => c.name === "properties")!.badgeCount =
        user?.badges?.unreadAnalysisCount
    }

    return config
  }, [user?.badges?.unreadAnalysisCount])
  const hideTabsForPath = HIDE_TABS_FOR_THESE_PATHS.includes(pathname)

  if (!authToken) {
    return <RegisterRedirect />
  }

  return (
    <OAuthProvider clientId={config.GOOGLE_WEB_CLIENT_ID}>
      <GestureHandlerRootView
        className="flex-1 web:flex web:flex-col web:max-h-dvh"
        style={{ flex: 1 }}
      >
        <BottomSheetModalProvider>
          {isDesktop && <BFDeskopHeader />}
          <Tabs
            screenListeners={{
              state: handleTabChange,
            }}
            screenOptions={{
              tabBarActiveTintColor: BFColors.calmBlue,
              tabBarInactiveTintColor: BFColors.midnightBlue,
              tabBarItemStyle: {
                paddingTop: 4,
              },
              tabBarStyle: {
                display: hideTabsForPath ? "none" : "flex",
                height: TAB_BAR_HEIGHT + bottom,
              },
              headerShown: false,
            }}
            tabBar={isDesktop ? () => null : undefined}
            backBehavior="history"
          >
            {iconConfig.map((item, i) => (
              <Tabs.Screen
                key={item.name}
                {...createTabScreenProps({ activeTab, ...item })}
              />
            ))}
          </Tabs>
        </BottomSheetModalProvider>
      </GestureHandlerRootView>
    </OAuthProvider>
  )
}
